import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IChat, MessageStatusEnum } from "@/types/IChat";
import { ItemLocation } from "@virtuoso.dev/message-list";
import { getChatroomContentV2 } from "@/services/getChatRoomContentV2";
import store from "@/redux/store";
import { getChatroomSearch } from "@/services/getChatroomSearch";
import { postPinTrenchMessage } from "@/services/postPinTrenchMessage";
import { getBroadcastPinnedMessage } from "@/services/getBroadcastPinnedMessage";
import { IBroadCastChannel } from "@/types/IBroadcastChannel";

export interface ChatSliceState {
  messages: IChat[];
  loading: boolean;
  dataSetKey: number;
  initialLocation: ItemLocation;
  hasNewer: boolean;
  isInMiddle: boolean;
  around: boolean;
  searchResults: IChat[];
  searchIndex: number;
  toggleSearch: boolean;
  toggleBottomSheet: boolean;
  chatMessageId: number;
  searchLoading: boolean;
  pinnedMessage: IChat;
  cult: IBroadCastChannel;
}

const initialChatSliceState: ChatSliceState = {
  messages: [],
  loading: false,
  dataSetKey: Date.now(),
  initialLocation: { index: "LAST", align: "end" },
  hasNewer: false,
  isInMiddle: false,
  around: false,
  searchResults: [],
  toggleSearch: false,
  toggleBottomSheet: false,
  chatMessageId: -1,
  searchIndex: 0,
  searchLoading: false,
  pinnedMessage: {} as IChat,
  cult: {} as IBroadCastChannel,
};

interface PinMessagePayload {
  channelId: number;
  messageId: number;
}


export const setPinMessage = createAsyncThunk(
  "chat/setPinnedMessage",
  async ({ channelId, messageId }: PinMessagePayload) => {
    let response = null;
    if (messageId === 0)
      response = await postPinTrenchMessage(0, channelId);
    else response = await postPinTrenchMessage(messageId, channelId);
    if (response && response.status === 201 && messageId !== 0) {
      return messageId;
    } else {
      return null;
    }
  }
);

export const loadInitialMessages = createAsyncThunk(
  "chat/loadInitialMessages",
  async (channelId: number) => {
    const messages = await getChatroomContentV2(channelId.toString());
    return messages
  },
);

const goToMessageWindow = createAsyncThunk(
  "chat/goToMessageWindow",
  async ({ key, channelId }: { key: number, channelId: number }, thunkApi) => {
    const messages = await getChatroomContentV2(channelId.toString(), `around=${key}`);
    const index = messages.findIndex((x) => x.messageId === key);
    thunkApi.dispatch(highlightMessage({ key, index }));
    return messages;
  },
);

export const loadOlderMessages = createAsyncThunk(
  "chat/loadOlderMessages",
  async (channelId: number, thunkApi) => {
    const state = (thunkApi.getState() as any).trenchesChatSliceV2 as ChatSliceState;
    const firstMessage = state.messages[0];
    if (firstMessage) {
      const messages = await getChatroomContentV2(channelId.toString(), `before=${firstMessage.messageId}`);
      return messages
    } else {
      return [];
    }
  },
);

export const recieveNewMessage = createAsyncThunk(
  "chat/recieveNewMessage",
  async (newMessage: IChat, thunkApi) => {
    return newMessage;
  },
);

export const deleteMessage = createAsyncThunk(
  "chat/deleteMessage",
  async (deleteMessageObj: IChat, thunkApi) => {
    return deleteMessageObj;
  },
);

export const editMessage = createAsyncThunk(
  "chat/editMessage",
  async (editMessageObj: { messageId: number, NewContent: string }, thunkApi) => {
    return editMessageObj;
  },
);

export const loadNewerMessages = createAsyncThunk(
  "chat/loadNewerMessages",
  async (channelId: number, thunkApi) => {
    const state = (thunkApi.getState() as any).trenchesChatSliceV2 as ChatSliceState;
    const lastMessage = state.messages[state.messages.length - 1];
    if (lastMessage) {
      const messages = await getChatroomContentV2(channelId.toString(), `after=${lastMessage.messageId}`);
      return messages
    } else {
      return [];
    }
  },
);

export const searchChatRoom = createAsyncThunk(
  "chat/searchChatRoom",
  async (data: { channelId: number, query: string }, thunkApi) => {
    const messages = await getChatroomSearch(data.channelId.toString(), data.query);
    return messages.reverse();
  },
);

export const loadPinnedMessage = createAsyncThunk(
  "chat/loadPinnedMessage",
  async (channelId: number) => {
    const messages = await getBroadcastPinnedMessage(channelId);
    if (messages && messages.length > 0) {
      return messages[0];
    } else {
      return null;
    }
  }
);

export const scrollToMessage = createAsyncThunk(
  "chat/scrollToMessage",
  async ({ key, channelId }: { key: number, channelId: number }, thunkApi) => {
    const state = (thunkApi.getState() as any).trenchesChatSliceV2 as ChatSliceState;
    const index = state.messages.findIndex(
      (message) => message.messageId === key,
    );
    if (index > -1) {
      thunkApi.dispatch(highlightMessage({ key, index }));
    } else {
      thunkApi.dispatch(goToMessageWindow({ key, channelId }));
    }
    return { index, align: "center" as "center" };
  },
);

export const highlightMessage = createAsyncThunk(
  "chat/highlightMessage",
  async ({ key, index }: { key: number, index: number }) => {
    return { key, index }
  },
);

export const TrenchesChatSliceV2 = createSlice({
  name: "chat",
  initialState: initialChatSliceState,
  reducers: {
    setToggleSearch: (state, action: PayloadAction<boolean>) => {
      state.toggleSearch = action.payload;
      if (!action.payload) {
        state.searchResults = [];
        state.searchIndex = 0;
      }
    },
    setCultInRedux: (state, action: PayloadAction<IBroadCastChannel>) => {
      state.cult = action.payload;
    },
    setBottomSheet: (state, action: PayloadAction<number>) => {
      state.chatMessageId = action.payload;
    },
    resetChatMessages: (state) => {
      state.messages = [];
      state.pinnedMessage = {} as IChat;
    },
    updateSearchIndex: (state, action: PayloadAction<number>) => {
      state.searchIndex = action.payload;
    },
    setIsInMiddle: (state, action: PayloadAction<boolean>) => {
      state.isInMiddle = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(setPinMessage.fulfilled, (state, action) => {
      if (action.payload) {
        const message = state.messages.find((x) => x.messageId == action.payload);
        console.log("Pinned message", message);
        if (message) {
          state.pinnedMessage = { ...message, isPinned: 1 };
          state.messages = state.messages.map((x) => {
            if (x.messageId === action.payload) {
              x.isPinned = 1;
            }
            return x;
          });
        }
      }
      else {
        state.pinnedMessage = {} as IChat;
        state.messages = state.messages.map((x) => {
          x.isPinned = 0;
          return x;
        });
      }
    });
    builder.addCase(loadPinnedMessage.fulfilled, (state, action) => {
      if (action.payload)
        state.pinnedMessage = {
          ...action.payload,
          messageId: action.payload.id,
          message: action.payload.textContent,
          outId: 0,
          replyToObject: {} as IChat,
          messageStatus: -1,
          highlighted: false,
        };
    });
    builder.addCase(loadInitialMessages.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(loadInitialMessages.fulfilled, (state, action) => {
      state.loading = false;
      state.dataSetKey = Date.now();
      state.messages = action.payload;
      state.isInMiddle = false;
      state.around = false;
    });
    builder.addCase(loadOlderMessages.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(loadOlderMessages.fulfilled, (state, action) => {
      state.loading = false;
      state.isInMiddle = true;
      state.messages = [...action.payload, ...state.messages];
    });
    builder.addCase(loadNewerMessages.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(loadNewerMessages.fulfilled, (state, action) => {
      state.loading = false;
      state.isInMiddle = false;
      state.around = false;
      state.messages = [...state.messages, ...action.payload];
    });
    builder.addCase(searchChatRoom.pending, (state) => {
      state.searchLoading = true;
      state.searchIndex = 0;
      state.searchResults = [];
    });
    builder.addCase(searchChatRoom.fulfilled, (state, action) => {
      state.searchLoading = false;
      state.isInMiddle = false;
      state.around = false;
      state.searchResults = action.payload;
    });
    builder.addCase(searchChatRoom.rejected, (state, action) => {
      state.searchLoading = false;
    });
    builder.addCase(recieveNewMessage.fulfilled, (state, action) => {
      if (action.payload) {
        state.messages = [...state.messages, action.payload];
        state.hasNewer = false;
      }
    });
    builder.addCase(deleteMessage.fulfilled, (state, action) => {
      state.messages.filter((chat) => {
        if (
          chat.messageStatus === MessageStatusEnum.REPLIED_MESSAGE &&
          chat.replyToObject.messageId === action.payload.messageId
        ) {
          chat.messageStatus =
            MessageStatusEnum.ORIGINAL_MESSAGE_DELETED;
        }
        return chat.messageId !== action.payload.messageId;
      });
    });
    builder.addCase(editMessage.fulfilled, (state, action) => {
      state.messages.filter((chat) => {
        if (chat.messageId === action.payload.messageId) {
          chat.message = action.payload.NewContent;
        }
        return chat;
      })
    });
    builder.addCase(goToMessageWindow.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(goToMessageWindow.fulfilled, (state, action) => {
      state.loading = false;
      state.messages = action.payload;
      const key = action.meta.arg.key;
      const index = state.messages.findIndex((message) => message.id === key);
      // reload the list, we're in a different window
      state.dataSetKey = Date.now();
      state.initialLocation = { index, align: "center" };
      state.hasNewer = true;
      state.isInMiddle = true;
      state.around = true;
    }); 
  },
});

export const {
  setToggleSearch,
  setBottomSheet,
  resetChatMessages,
  updateSearchIndex,
  setIsInMiddle,
  setCultInRedux
} = TrenchesChatSliceV2.actions;

export default TrenchesChatSliceV2.reducer;